import React, { useMemo } from 'react';
import moment from 'moment';

import EmptyData from '../../../components/UI/EmptyData';

import './CelebrationHumanList.scss';

const AVATAR_COLORS = ['#22c55e', '#6366f1', '#8b5cf6', '#ec4899', '#0ea5e9', '#d97706', '#64748b'];

const initialsFrom = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const colorForName = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i += 1) {
    h = (h + s.charCodeAt(i) * (i + 3)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[h];
};

const designationOf = (person) =>
  person?.designation || person?.designations?.name || person?.designations?.[0]?.name || '';

const occasionThisYear = (rawDate) => {
  const m = moment(rawDate);
  if (!m.isValid()) return null;
  return m.clone().year(moment().year()).startOf('day');
};

const turningAge = (person) => {
  const dob = moment(person?.dateOfBirth);
  if (!dob.isValid()) return null;
  const occ = occasionThisYear(person.dateOfBirth);
  if (!occ) return null;
  const y = occ.diff(dob.startOf('day'), 'years');
  return y + 1;
};

const serviceYears = (person) => {
  const join = moment(person?.dateOfJoining).startOf('day');
  if (!join.isValid()) return null;
  const occ = occasionThisYear(person.dateOfJoining);
  if (!occ) return null;
  return occ.diff(join, 'years');
};

const humanWhenHiring = (person) => {
  const join = moment(person?.dateOfJoining);
  if (!join.isValid()) return 'Recently joined';
  const daysAgo = moment().startOf('day').diff(join.clone().startOf('day'), 'days');
  if (daysAgo === 0) return 'Joined today';
  if (daysAgo === 1) return 'Joined yesterday';
  if (daysAgo > 1 && daysAgo <= 30) return `Joined ${daysAgo} days ago`;
  return `Joined on ${join.format('ddd, MMM D')}`;
};

const humanWhenBirthday = (person, daysLeft) => {
  const occ = occasionThisYear(person?.dateOfBirth);
  const datePhrase = occ ? occ.format('dddd, MMM D') : '';
  const age = turningAge(person);

  if (daysLeft === 0) {
    return age ? `Birthday today` : 'Birthday today — a great day to say hello';
  }
  if (daysLeft === 1) {
    return age ? `Birthday tomorrow ` : 'Birthday tomorrow';
  }
  return age ? `${datePhrase}` : `Coming up ${datePhrase}`;
};

const humanWhenAnniversary = (person, daysLeft) => {
  const occ = occasionThisYear(person?.dateOfJoining);
  const datePhrase = occ ? occ.format('dddd, MMM D') : '';
  const years = serviceYears(person);

  if (daysLeft === 0) {
    if (years != null && years > 0) return `${years}-year milestone today`;
    if (years === 0) return 'First anniversary with the team today';
    return 'Work anniversary today — time to celebrate';
  }
  if (daysLeft === 1) {
    if (years != null && years > 0) return `${years} years tomorrow`;
    if (years === 0) return 'First anniversary tomorrow';
    return 'Work anniversary tomorrow';
  }
  if (years != null && years > 0) return `${years} years on ${datePhrase}`;
  if (years === 0) return `First anniversary · ${datePhrase}`;
  return `Mark the calendar · ${datePhrase}`;
};

const toneLine = (variant, daysLeft) => {
  if (variant === 'birthday') {
    if (daysLeft === 0) return 'Make their day a little brighter';
    if (daysLeft === 1) return 'Almost here — plan a quick shout-out';
    return 'Save the date for your team';
  }
  if (variant === 'hiring') {
    return 'Say hi and help them get settled in';
  }
  if (daysLeft === 0) return 'Say thanks for the journey so far';
  if (daysLeft === 1) return 'A good moment for recognition';
  return 'Recognition goes a long way';
};

const buildRows = (items, variant, isBirthday) =>
  (items || []).map((person, i) => {
    const name = person?.name || 'Unknown';
    const key = person?._id || person?.id || `${name}-${i}`;
    const daysLeft = person?.daysLeft ?? person?._daysLeft ?? 0;
    const isHiring = variant === 'hiring';
    const safeDaysLeft = typeof daysLeft === 'number' && Number.isFinite(daysLeft) ? daysLeft : 0;
    const sub = isHiring
      ? humanWhenHiring(person)
      : isBirthday
        ? humanWhenBirthday(person, safeDaysLeft)
        : humanWhenAnniversary(person, safeDaysLeft);
    const tone = toneLine(variant, safeDaysLeft);
    const badge = isHiring
      ? 'New'
      : safeDaysLeft === 0
        ? 'Today'
        : safeDaysLeft === 1
          ? 'Tomorrow'
          : `In ${safeDaysLeft} days`;
    return { key, person, name, daysLeft, sub, tone, badge };
  });

/** Shared card UI (list + snap slider). */
export const CelebrationPersonCard = ({ variant, person, name, daysLeft, sub, tone, badge }) => {
  const src = person?.profilePicture;
  const initials = initialsFrom(name);
  const role = designationOf(person);

  return (
    <article
      className={`celebration-human__item celebration-human__item--${variant} ${
        daysLeft === 0 ? 'is-today' : ''
      }`}
    >
      <div className="celebration-human__avatar-wrap">
        {src ? (
          <img className="celebration-human__avatar" src={src} alt="" />
        ) : (
          <span
            className="celebration-human__avatar celebration-human__avatar--initials"
            style={{ background: colorForName(name) }}
            aria-hidden
          >
            {initials}
          </span>
        )}
      </div>
      <div className="celebration-human__content">
        <p className="celebration-human__name">{name}</p>
        {role ? <p className="celebration-human__role">{role}</p> : null}
        <p className="celebration-human__sub">{sub}</p>
        <p className="celebration-human__tone">{tone}</p>
      </div>
      <span
        className={`celebration-human__badge ${
          daysLeft === 0 ? 'celebration-human__badge--today' : ''
        }`}
      >
        {badge}
      </span>
    </article>
  );
};

export const buildCelebrationRows = (items, variant) => {
  const isBirthday = variant === 'birthday';
  return buildRows(items, variant, isBirthday);
};

/**
 * Rich, readable celebration rows (birthdays / work anniversaries).
 */
const CelebrationHumanList = ({ variant = 'birthday', items = [] }) => {
  const isBirthday = variant === 'birthday';

  const { todayRows, laterRows } = useMemo(() => {
    const rows = buildRows(items, variant, isBirthday);
    return {
      todayRows: rows.filter((r) => r.daysLeft === 0),
      laterRows: rows.filter((r) => r.daysLeft > 0)
    };
  }, [items, variant, isBirthday]);

  const hasAny = todayRows.length > 0 || laterRows.length > 0;

  if (!hasAny) {
    return (
      <div className="celebration-human celebration-human--empty px-2 py-2">
        <EmptyData compact />
      </div>
    );
  }

  const sectionLaterLabel = isBirthday ? 'Birthdays this week' : 'Anniversaries this week';

  return (
    <div className="celebration-human celebration-human--stack">
      {todayRows.length > 0 ? (
        <section className="celebration-human__section" aria-label="Today">
          <h3 className="celebration-human__section-title">Today</h3>
          <ul className="celebration-human__list" role="list">
            {todayRows.map((r) => (
              <li key={r.key} className="celebration-human__list-cell" role="listitem">
                <CelebrationPersonCard variant={variant} {...r} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {laterRows.length > 0 ? (
        <section className="celebration-human__section" aria-label={sectionLaterLabel}>
          <h3
            className={`celebration-human__section-title${
              todayRows.length > 0 ? ' celebration-human__section-title--muted' : ''
            }`}
          >
            {sectionLaterLabel}
          </h3>
          <ul className="celebration-human__list" role="list">
            {laterRows.map((r) => (
              <li key={r.key} className="celebration-human__list-cell" role="listitem">
                <CelebrationPersonCard variant={variant} {...r} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};

export default CelebrationHumanList;
